import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { formatKwota } from '../utils';
import i18n from '../i18n';
import {
  priceRangeText, cheapestSellerText, cheapestOfferUrl, asOfText,
  unitPriceText, segmentMedianText, availabilityText,
} from '../utils/marketStats';

const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== '';

// Pytania budowane wyłącznie z pól, które faktycznie istnieją w market_stats —
// brakujące dane = pominięte pytanie, nigdy null/undefined w treści.
const buildQuestions = (marketStats, modelName, category) => {
  if (!marketStats) return [];
  const qs = [];

  const priceCore = priceRangeText(marketStats);
  if (priceCore) {
    const asOf = asOfText(marketStats);
    qs.push({
      q: i18n.t('model:faq.ile_kosztuje', { model: modelName }),
      a: asOf ? i18n.t('model:faq.odp_cena_stan', { cena: priceCore, data: asOf }) : `${priceCore}.`,
    });
  }

  const seller = cheapestSellerText(marketStats);
  const minPrice = hasValue(marketStats.price_min) && Number(marketStats.price_min) > 0
    ? formatKwota(marketStats.price_min, 0) : null;
  if (seller && minPrice !== null) {
    qs.push({
      q: i18n.t('model:faq.gdzie_kupic', { model: modelName }),
      a: i18n.t('model:faq.odp_gdzie', { kwota: minPrice, sklep: seller }),
      url: cheapestOfferUrl(marketStats),
    });
  }

  const avail = availabilityText(marketStats);
  if (avail) {
    qs.push({ q: i18n.t('model:faq.czy_dostepny', { model: modelName }), a: `${avail}.` });
  }

  if (marketStats.unit_price?.unit === 'Wp') {
    const unitText = unitPriceText(marketStats);
    if (unitText) {
      const median = segmentMedianText(marketStats, category);
      qs.push({
        q: i18n.t('model:faq.za_wp', { model: modelName }),
        a: median ? `${unitText}. ${median}.` : `${unitText}.`,
      });
    }
  }

  return qs;
};

const MarketFaq = ({ marketStats, modelName, category }) => {
  const { t } = useTranslation('model');
  const questions = buildQuestions(marketStats, modelName, category);
  if (!questions.length) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6 mb-16">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-7">
        <h2 className="text-sm font-bold text-gray-900 mb-3">{t('faq.tytul')}</h2>
        <div className="divide-y divide-gray-100">
          {questions.map((item, i) => (
            <details key={i} className="group py-3 first:pt-0 last:pb-0">
              <summary className="flex items-center justify-between gap-3 cursor-pointer list-none text-sm font-semibold text-gray-800">
                {item.q}
                <FontAwesomeIcon icon={faChevronDown} className="text-gray-400 text-xs transition-transform group-open:rotate-180 shrink-0" />
              </summary>
              <p className="mt-2 text-sm text-gray-600 leading-6">
                {item.a}
                {item.url && (
                  <>
                    {' '}
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-blue-600 font-semibold hover:underline"
                    >
                      {t('zobacz_oferte')}
                    </a>
                  </>
                )}
              </p>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MarketFaq;
